import { Row, Col, Space, Typography, InputNumber, Select } from "antd";
import { ColorPickerWithLabel } from "@components/Settings/OverviewSettings/ColorPickerWithLabel";
import type { GaugeSegmentConfig } from "../../Processing/types";

const { Option } = Select;

interface GaugeSegmentFormProps {
  value: GaugeSegmentConfig;
  onChange: (config: GaugeSegmentConfig) => void;
}

const DEFAULT_COLORS = ["#7CFC00", "#FFD700", "#FF8C00", "#FF0000", "#8B0000"];
const DEFAULT_THRESHOLDS_BY_SEGMENTS: Record<number, number[]> = {
  2: [0.5],
  3: [0.33, 0.66],
  4: [0.25, 0.5, 0.75],
  5: [0.2, 0.35, 0.5, 0.65],
};

export function GaugeSegmentForm({ value, onChange }: GaugeSegmentFormProps) {
  const hasInvalidThresholds = value.thresholds.some(
    (t, i) => i > 0 && t <= value.thresholds[i - 1],
  );

  // ── Segments change ──────────────────────────────────────────
  const handleSegmentsChange = (segments: number) => {
    const colors = Array.from(
      { length: segments },
      (_, i) => value.colors[i] ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length],
    );
    const thresholds =
      DEFAULT_THRESHOLDS_BY_SEGMENTS[segments] ?? value.thresholds.slice(0, segments - 1);
    onChange({ segments, colors, thresholds });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {/* Segment count */}
      <Row align="middle" gutter={8}>
        <Col span={8}>
          <Typography.Text>Segments</Typography.Text>
        </Col>
        <Col span={8}>
          <Select size="small" value={value.segments} onChange={handleSegmentsChange} style={{ width: "50%" }}>
            {[2, 3, 4, 5].map((n) => (
              <Option key={n} value={n}>{n}</Option>
            ))}
          </Select>
        </Col>
      </Row>

      {/* Colors */}
      <Row align="middle" gutter={8}>
        <Col span={8}>
          <Typography.Text>Colors</Typography.Text>
        </Col>
        <Col span={16}>
          <Space size={[8, 8]} wrap className="gauge-color-swatches">
            {value.colors.slice(0, value.segments).map((color, i) => (
              <ColorPickerWithLabel
                key={i}
                pickerId={`gaugeSegColor${i}`}
                defaultColor={color}
                usePopover
                handleColorChangeParent={(_: string, newColor: string) => {
                  const next = [...value.colors];
                  next[i] = newColor;
                  onChange({ ...value, colors: next });
                }}
              />
            ))}
          </Space>
        </Col>
      </Row>

      {/* Thresholds — capped at 4 (max 5 segments), laid out in a single row */}
      <Row gutter={8}>
        <Col span={8}>
          <Typography.Text>Thresholds (0–1)</Typography.Text>
        </Col>
        <Col span={16}>
          <Space size={6} wrap align="center">
            {value.thresholds.map((t, i) => (
              <InputNumber
                key={i}
                size="small"
                min={0}
                max={1}
                step={0.01}
                precision={2}
                value={t}
                status={i > 0 && t <= value.thresholds[i - 1] ? "error" : undefined}
                onChange={(v) => {
                  if (v == null) return;
                  const next = [...value.thresholds];
                  next[i] = v;
                  onChange({ ...value, thresholds: next });
                }}
                style={{ width: 64 }}
              />
            ))}
          </Space>
          {hasInvalidThresholds && (
            <Typography.Text type="danger" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
              Each threshold must be greater than the previous one.
            </Typography.Text>
          )}
        </Col>
      </Row>
    </Space>
  );
}
